import { Component, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { OrderPrestadorService } from 'src/app/services/order-prestador.service';
import { Location } from '@angular/common';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { API_URI } from 'src/environments/environment';

@Component({
    selector: 'app-resumen-order-prestador',
    templateUrl: './resumen-order.component.html',
    styleUrls: ['./resumen-order.component.css'],
    standalone: false
})
export class ResumenOrderPrestadorComponent {
  private _orderPrestadorService = inject(OrderPrestadorService);
  private _router = inject(Router);
  private _location = inject(Location);
  private _authService = inject(AuthService);
  private _loadingService = inject(LoadingService);
  
  error = signal<string>('');
  loadingLocal = signal<boolean>(false);

  helpOpen = signal<boolean>(false);
  helpText = signal<string>('Este es el resúmen de tu solicitud. Chequeá que todo esté correcto antes de enviarlo.');

  id_especialidad?: number | null;
  especialidadDescripcion: string = '';

  id_prestador?: string | null;
  prestadorDescripcion: string = '';

  id_prestacion?: number | null;
  prestacionDescripcion: string = '';

   x_zona: string = '';
  lugarDescripcion: string = '';

  // Encabezado armado del resumen (prestador puntual, o especialidad/prestación si no hay prestador)
  tituloResumen: string = '';
  subtitulosResumen: string[] = [];
  iconoResumen: string = 'bi-person-badge';
  avatarPrestadorUrl?: string;

  d_fecha_uno?: Date;
  d_fecha_dos?: Date | null;
  d_fecha_tres?: Date | null;

  b_manana: boolean = false;
  b_tarde: boolean = false;
  b_noche: boolean = false;
 
  isFinished = signal<boolean>(false);
  n_cantidad_ordenes_enviadas = signal<number>(0);

  img_receta?: File;
  preview_img_receta?: string;

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;
    await this._loadingService.run(async () => {
      try {
        await this._authService.ensureSession();

        if (this._authService.session() === 'guest') {
          this._router.navigate(['/sign-in']);
          return;
        }

        const ord = this._orderPrestadorService.ordenPrestador;
        // ORDEN VACÍA → volver al dashboard
      if (!ord || !ord.id_especialidad && !ord.id_prestador && !ord.id_prestacion) {
        this._router.navigate(['/dashboard']);
        return;
      }
      
        if (ord.id_especialidad) {
          this.id_especialidad = ord.id_especialidad;
          this.especialidadDescripcion =
            this._orderPrestadorService.especialidades
              .find(e => e.id == this.id_especialidad)?.x_descripcion ?? '';
        }

        if (ord.id_prestador) {
          this.id_prestador = ord.id_prestador;

          const prestadorTemp =
            this._orderPrestadorService.prestadores
              .find(p => p.id == this.id_prestador);

          if (prestadorTemp) {
            this.prestadorDescripcion =
              `${prestadorTemp.x_nombre} ${prestadorTemp.x_apellido}`;

            this.avatarPrestadorUrl = prestadorTemp.x_img_avatar
              ? API_URI + 'uploads/images/small-' + prestadorTemp.x_img_avatar
              : undefined;
          }
        }

        if (ord.id_prestacion) {
          this.id_prestacion = ord.id_prestacion;

          this.prestacionDescripcion =
            this._orderPrestadorService.prestaciones
              .find(p => p.id == this.id_prestacion)?.x_descripcion ?? '';
        }

        // Encabezado: prestador puntual si lo hay, sino especialidad/prestación
        if (this.prestadorDescripcion) {
          this.tituloResumen = this.prestadorDescripcion;
          this.iconoResumen = 'bi-person-badge';
          this.subtitulosResumen = [this.especialidadDescripcion, this.prestacionDescripcion].filter(s => !!s);
        } else {
          this.tituloResumen = this.especialidadDescripcion || this.prestacionDescripcion || 'Tu solicitud de turno';
          this.iconoResumen = 'bi-clipboard2-pulse';
          this.subtitulosResumen =
            this.especialidadDescripcion && this.prestacionDescripcion && this.especialidadDescripcion !== this.prestacionDescripcion
              ? [this.prestacionDescripcion]
              : [];
        }

        this.x_zona = ord.x_zona ?? '';

        const ubicacion = this._orderPrestadorService.ubicacionSeleccionada;
        if (ubicacion) {
          this.lugarDescripcion = [ubicacion.x_domicilio, ubicacion.x_piso_dpto, ubicacion.nombreProvincia]
            .filter(parte => !!parte?.trim())
            .join(', ');
        }

        this.d_fecha_uno = ord.d_fecha_uno ?? undefined;
        this.d_fecha_dos = ord.d_fecha_dos ?? null;
        this.d_fecha_tres = ord.d_fecha_tres ?? null;

        this.b_manana = !!ord.b_manana;
        this.b_tarde = !!ord.b_tarde;
        this.b_noche = !!ord.b_noche;

        // restaurar receta
        this.img_receta = this._orderPrestadorService.img_receta;
        this.preview_img_receta = this._orderPrestadorService.preview_img_receta;

      } catch (e: any) {
        console.error(e);
        this.error.set(e?.message ?? 'No se pudo cargar la orden.');
      }
    });
  }

  async finalizaOrder(): Promise<void> {
    this.error.set('');
    this.loadingLocal.set(true);
    try {
      await this._loadingService.run(async () => {
        const res = await this._orderPrestadorService.finalizeOrdenPrestador();
        this.n_cantidad_ordenes_enviadas.set(res.n_cantidad_ordenes);
        this.isFinished.set(true);
      });

    } catch (error: any) {
      if (error?.codigo === 1) {
        this.error.set(error.error);
      } else if (error?.message) {
        this.error.set(error.message);
      } else {
        this.error.set('Lo sentimos ha ocurrido un error.');
      }
    } finally {
      this.loadingLocal.set(false);
    }
  }

  openhelp(): void {
    this.helpOpen.update(v => !v);
  }

  back(): void {
    this._location.back();
  }

  /**
   * Cámara / Galería:
   * - Nativo: Prompt (cámara o galería)
   * - Web/PWA: Photos (archivo/galería)
   */
  async capturarFileImgReceta(): Promise<void> {
    try {
      const source = Capacitor.isNativePlatform()
        ? CameraSource.Prompt
        : CameraSource.Photos;

      const photo = await Camera.getPhoto({
        resultType: CameraResultType.DataUrl,
        source,
        quality: 90,
        allowEditing: false,
        promptLabelHeader: 'Seleccionar imagen',
        promptLabelPhoto: 'Galería',
        promptLabelPicture: 'Cámara',
      });

      if (!photo.dataUrl) return;

      this.preview_img_receta = photo.dataUrl;

      // base64 -> blob -> File
      const response = await fetch(photo.dataUrl);
      const blob = await response.blob();
      this.img_receta = new File([blob], 'receta.jpg', { type: blob.type || 'image/jpeg' });

      this._orderPrestadorService.img_receta = this.img_receta;
      this._orderPrestadorService.preview_img_receta = this.preview_img_receta;
    } catch (error) {
      // si el usuario cancela, Capacitor suele tirar error -> lo ignoramos
      console.error('Captura cancelada o error:', error);
    }
  }

  // Input file (por compatibilidad web)
  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.error.set('El archivo seleccionado no es una imagen');
      input.value = '';
      return;
    }

    this.error.set('');
    this.img_receta = file;
    this.preview_img_receta = await this.fileToDataUrl(file);
    this._orderPrestadorService.img_receta = this.img_receta;
    this._orderPrestadorService.preview_img_receta = this.preview_img_receta;

    // para permitir volver a elegir el mismo archivo
    input.value = '';
  }

  private fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
    });
  }
}
